<template>
  <div class="w-full pt-5">
    <template>
      <div class="flex items-center">
        <div class="border-r border-romanSilver h-6 flex">
          <BackButton
            label="Back"
            @onClick="goBackToDirectReport()"
            variant="secondary"
            class="ml-2"
          />
        </div>
        <h1 class="text-xl text-left font-extrabold mx-4">
          Employee Appraisal
        </h1>
        <Breadcrumb :items="breadcrumbs" />
      </div>
    </template>
    <template v-if="isLoading">
      <div class="mt-20 h-full">
        <Loader size="xxs" :loader-image="false" />
      </div>
    </template>

    <template v-else>
      <template>
        <Card class="px-5 py-2 flex flex-col m-5">
          <div class="flex justify-between items-center m-1">
            <div
              class="flex gap-2 mb-2 font-bold text-sm leading-6 tracking-normal"
            >
              <span class="uppercase text-romanSilver">Employee:</span>
              <span class="capitalize text-darkPurple">{{
                employees.name
              }}</span>
            </div>
          </div>
          <div class="flex gap-5">
            <div class="flex-1 w-2/12 rounded-md">
              <img
                v-if="employees.photo !== '' && employees.photo !== null"
                :src="employees.photo"
                alt="profile"
                style="height: 183px; width: 183px; border-radius: 5px"
              />
              <div
                style="height: 183px; width: 183px; border-radius: 5px"
                class="text-blueCrayola border text-center font-black text-lg pt-14 mr-2"
                v-else
              >
                {{ $getInitials(`${employees.name}`) }}
              </div>
            </div>
            <div class="flex flex-col flex-3 w-10/12">
              <div class="flex mb-2 w-full gap-2">
                <div
                  class="flex flex-1 p-3 shadow rounded-md gap-2 items-start"
                >
                  <Icon
                    class-name="text-romanSilver"
                    size="xs"
                    icon-name="calendar"
                  />
                  <div class="flex flex-col tracking-wide font-bold">
                    <span class="text-xs text-carrotOrange uppercase"
                      >Designation</span
                    >
                    <span class="text-sm text-jet uppercase">{{
                      employees.designation
                    }}</span>
                  </div>
                </div>
                <div
                  class="flex flex-1 p-3 shadow rounded-md gap-2 items-start"
                >
                  <Icon
                    class-name="text-romanSilver"
                    size="xs"
                    icon-name="icon-department"
                  />
                  <div class="flex flex-col tracking-wide font-bold">
                    <span class="text-xs text-blueCrayola uppercase"
                      >Function</span
                    >
                    <span class="text-sm text-jet uppercase">{{
                      employees.function
                    }}</span>
                  </div>
                </div>
                <div
                  class="flex flex-1 p-3 shadow rounded-md gap-2 items-start"
                >
                  <Icon
                    class-name="text-romanSilver"
                    size="xs"
                    icon-name="user"
                  />
                  <div class="flex flex-col tracking-wide font-bold">
                    <span class="text-xs text-mediumSeaGreen uppercase"
                      >Line Manager</span
                    >
                    <span
                      class="text-sm text-jet uppercase whitespace-nowrap"
                      >{{ employees.manager }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="flex mb-4 w-full gap-2">
                <div
                  class="flex flex-1 p-3 shadow rounded-md gap-2 items-start w-1/3"
                >
                  <Icon
                    class-name="text-romanSilver"
                    size="xs"
                    icon-name="icon-user-list"
                  />
                  <div class="flex flex-col tracking-wide font-bold">
                    <span class="text-xs text-lite uppercase">Employee ID</span>
                    <span class="text-sm text-jet uppercase">{{
                      employees.employeeId
                    }}</span>
                  </div>
                </div>
                <div
                  class="flex flex-2 p-3 shadow rounded-md gap-2 items-start w-2/3"
                >
                  <Icon
                    class-name="text-romanSilver"
                    size="xs"
                    icon-name="pin"
                  />
                  <div class="flex flex-col tracking-wide font-bold">
                    <span class="text-xs text-flame uppercase"
                      >Office Location</span
                    >
                    <span class="text-sm text-jet uppercase">{{
                      employees.office
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </template>
      <template>
        <div class="mx-5 mt-10">
          <Tab
            class="mt-6 mx-3"
            :tabs="tabsItems"
            :active-tab="activeTab"
            @currentTab="activateTab($event)"
            :border="true"
          />

          <StatusReportView
            :id="$route.params.userId"
            hide-overview
            v-if="activeTab === 'Status Report Overview'"
            :hide-tab="true"
          />

          <div v-if="startAppraisal && activeTab !== 'Status Report Overview'">
            <template>
              <div class="mt-20 h-full" v-if="loadingAppraisal">
                <Loader size="xxs" :loader-image="false" />
              </div>

              <div class="my-3 px-2" v-else>
                <div
                  class="w-full flex flex-row justify-between"
                  style="margin-top: 20px"
                >
                  <div class="flex h-auto mt-2">
                    <h1 class="text-base font-bold">KPAs</h1>
                  </div>

                  <div class="w-auto flex items-center gap-2">
                    <div
                      class="w-auto flex cursor-pointer items-center"
                      @click="getEachAgreementComments()"
                    >
                      <Icon
                        icon-name="comment-plus-icon"
                        style="color: white"
                        size="xsm"
                      />
                    </div>
                    <div class="h-4 mx-3 w-px bg-gray-400" />
                    <div
                      class="flex items-center justify-between p-2 bg-ghostWhite rounded"
                    >
                      <span class="text-sm font-black whitespace-nowrap">
                        Total Allocated Weight:
                      </span>
                      <span class="text-sm text-desire ml-2">
                        {{ allocatedWeight }}%
                      </span>
                    </div>

                    <div class="self-stretch w-px bg-gray-400 mx-3"></div>

                    <div
                      class="flex items-center justify-between p-2 bg-ghostWhite rounded"
                    >
                      <span class="text-sm font-semibold">
                        {{ approvedScore || "---" }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="w-full h-px bg-gray-300 my-4"></div>

                <!--- List of KPAs-->

                <div class="w-full h-auto flex flex-col">
                  <ValidationObserver
                    rules="required"
                    v-slot="{ handleSubmit }"
                  >
                    <accordion
                      class="mt-6"
                      v-for="(kpaTypes, kpaNumber) in templateKPATypes"
                      :key="'kpa-' + kpaNumber"
                      show-divider
                      :is-open="true"
                    >
                      <template v-slot:title>
                        <span class="font-bold">{{ kpaTypes.name }}</span>
                      </template>

                      <template v-slot:block>
                        <div class="w-auto flex items-center">
                          <div class="w-auto flex items-center">
                            <Icon
                              icon-name="comment-plus-icon"
                              style="color: white"
                              size="xsm"
                            />
                          </div>

                          <div class="h-4 mx-2 w-px bg-gray-400" />

                          <div class="w-auto flex items-center">
                            <img
                              src="@/assets/images/hugeicons_assignments.svg"
                              style="width: 24px; height: 24px"
                            />
                          </div>

                          <div class="h-4 mx-2 w-px bg-gray-400" />

                          <div
                            class="py-1 px-2 w-auto flex gap-2 items-center bg-gray-50 shadow-sm rounded-sm"
                          >
                            <span class="font-black text-xs">
                              {{ kpaTypes.weight }}%
                            </span>
                          </div>

                          <div class="h-4 mx-2 w-px bg-gray-400" />

                          <div
                            class="p-2 w-auto flex gap-2 bg-gray-50 shadow-sm items-center rounded-sm mr-2"
                          >
                            <span class="font-black text-xs">
                              {{ kpaTypeScores[kpaTypes.id] || "---" }}
                            </span>
                          </div>
                        </div>
                      </template>

                      <div class="flex flex-col w-full -mt-3">
                        <div class="w-full gap-6 flex flex-col">
                          <accordion
                            class="px-5 -mt-1 bg-gray-50 shadow-md rounded-lg mb-0"
                            v-for="(item, index) in kpaTypes.kpas"
                            :key="'kpi-' + index"
                            :icon-class="'mt-2'"
                            :is-open="true"
                          >
                            <template v-slot:block>
                              <div
                                class="w-full flex items-center mt-2 justify-between"
                              >
                                <div class="w-auto flex">
                                  <div class="w-auto flex gap-2">
                                    <span class="font-bold text-flame text-sm"
                                      >KPI :</span
                                    >
                                    <span class="font-semibold text-sm">{{
                                      item.data?.name || item.name
                                    }}</span>
                                  </div>

                                  <div class="w-auto mx-4 flex gap-1">
                                    <span class="w-[1px] h-full border" />
                                  </div>

                                  <div class="w-auto flex gap-2">
                                    <span class="font-bold text-flame text-sm"
                                      >Objective :</span
                                    >
                                    <span class="font-semibold text-sm">{{
                                      item.data?.objective || item.objective
                                    }}</span>
                                  </div>
                                </div>

                                <div class="w-auto flex items-center">
                                  <div
                                    class="flex cursor-pointer items-center"
                                    @click="
                                      getEachAgreementComments(item.kpaId)
                                    "
                                  >
                                    <Icon
                                      icon-name="comment-plus-icon"
                                      style="color: white"
                                      size="xsm"
                                    />
                                  </div>

                                  <div
                                    :class="'h-6'"
                                    class="w-auto mx-4 flex items-center"
                                  >
                                    <span class="w-px bg-gray-300 h-6"></span>
                                  </div>

                                  <div
                                    class="flex cursor-pointer items-center"
                                    @click="viewReport(item, kpaTypes)"
                                  >
                                    <img
                                      v-if="
                                        item.report === null ||
                                        item.report === 'null'
                                      "
                                      src="@/assets/images/hugeicons_assignments-gray.svg"
                                      style="width: 24px; height: 24px"
                                    />
                                    <img
                                      v-else
                                      src="@/assets/images/hugeicons_assignments.svg"
                                      style="width: 24px; height: 24px"
                                    />
                                  </div>
                                  <div
                                    :class="'h-6'"
                                    class="w-auto mx-4 flex items-center"
                                  >
                                    <span class="w-px bg-gray-300 h-6"></span>
                                  </div>
                                  <div
                                    class="px-2 py-1 w-auto flex gap-2 items-center rounded-sm mt-1"
                                  >
                                    <span class="font-black text-xs">
                                      {{ item.data?.weight || item.weight }}%
                                    </span>
                                  </div>

                                  <div class="h-4 mx-2 w-px bg-gray-400" />

                                  <div
                                    class="p-2 w-auto flex gap-2 bg-gray-50 shadow-sm items-center rounded-sm mr-2"
                                  >
                                    <span class="font-black text-xs">
                                      {{
                                        getReviewType() === "final_review"
                                          ? item.approvedScore || "---"
                                          : item.approvedFlag || "---"
                                      }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </template>

                            <div
                              class="w-full h-[1px] mb-6 -mt-3 border border-2"
                            />

                            <!-- Labels row -->
                            <div
                              class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full mb-3"
                            >
                              <!-- KPI Criteria List -->
                              <div
                                class="flex flex-col"
                                v-for="(n, rating) in item.data?.kpis ||
                                item.kpis"
                                :key="rating"
                              >
                                <span
                                  v-if="rating === 0"
                                  class="text-sm font-semibold mb-1"
                                  >Criteria</span
                                >
                                <span v-else class="mb-1">&nbsp;</span>
                                <c-text
                                  variant="w-full"
                                  class="mr-2 -mt-2"
                                  :disabled="true"
                                  :value="n.criteria"
                                />
                              </div>

                              <!-- Employee Self Rating -->
                              <div class="flex flex-col w-full">
                                <span class="text-sm font-semibold mb-3"
                                  >Employee Self-Appraisal</span
                                >
                                <div class="w-full flex">
                                  <div class="w-full">
                                    <ValidationObserver rules="required">
                                      <div class="w-full flex flex-col gap-1">
                                        <div class="w-full flex bg-white -mt-2">
                                          <select
                                            class="input-select"
                                            v-model="item.selfRating"
                                            :disabled="true"
                                          >
                                            <option value="" disabled selected>
                                              --Rating--
                                            </option>
                                            <option
                                              v-for="option in activeRatingOptions"
                                              :value="option.id"
                                              :key="option.id"
                                            >
                                              {{ option.name }}
                                            </option>
                                          </select>
                                        </div>
                                      </div>
                                    </ValidationObserver>
                                  </div>
                                </div>
                              </div>

                              <!-- Line Manager Rating -->
                              <div class="flex w-full flex-col">
                                <span class="text-sm font-semibold mb-3"
                                  >Line Manager Appraisal</span
                                >
                                <div
                                  class="w-full flex bg-white -mt-2"
                                  v-if="
                                    startAppraisal &&
                                    lineManagerAppraisalScore &&
                                    !isDottedManager
                                  "
                                >
                                  <select
                                    @change="
                                      changeManagerRating(
                                        $event,
                                        item,
                                        templateKPATypes,
                                        kpaTypes.id,
                                        getReviewType()
                                      )
                                    "
                                    @input="setInput(kpaTypes.name)"
                                    class="input-select"
                                    :disabled="employee"
                                    v-model="item.managerRating"
                                    v-if="employee"
                                  >
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>

                                  <select
                                    class="input-select"
                                    v-model="item.managerRating"
                                    @change="
                                      onChangeLineManagerRating(
                                        $event,
                                        item,
                                        templateKPATypes,
                                        kpaTypes.id,
                                        getReviewType()
                                      )
                                    "
                                    @input="setInput(kpaTypes.name)"
                                    v-if="
                                      !employee &&
                                      appraisalStatus !== 'submitted' &&
                                      !item.hasChanged
                                    "
                                  >
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>

                                  <select
                                    class="input-select"
                                    v-model="item.ratingId"
                                    @change="
                                      changeManagerRating(
                                        $event,
                                        item,
                                        templateKPATypes,
                                        kpaTypes.id,
                                        getReviewType()
                                      )
                                    "
                                    @input="setInput(kpaTypes.name)"
                                    v-if="
                                      !employee &&
                                      appraisalStatus !== 'submitted' &&
                                      item.hasChanged
                                    "
                                  >
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>

                                  <select
                                    class="input-select"
                                    v-model="item.managerRating"
                                    @change="
                                      changeManagerRating(
                                        $event,
                                        item,
                                        templateKPATypes,
                                        kpaTypes.id,
                                        getReviewType()
                                      )
                                    "
                                    @input="setInput(kpaTypes.name)"
                                    v-if="
                                      !employee &&
                                      appraisalStatus === 'submitted'
                                    "
                                    :disabled="true"
                                  >
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>
                                </div>
                                <!-- <c-select placeholder="--Rating--" variant="w-full" class="-ml-1 -mt-4" type="number"
                                  :disabled="true" :options="activeRatingOptions" v-model="item.managerRating" v-else /> -->
                                <c-select
                                  :placeholder="
                                    item.managerRating ? `Appriased` : ``
                                  "
                                  variant="w-full"
                                  class="-ml-1 -mt-4"
                                  type="number"
                                  :disabled="true"
                                  v-else
                                  :options="activeRatingOptions"
                                />
                              </div>

                              <!-- Dotted Line Manager Rating -->
                              <div class="flex w-full flex-col" v-if="hasDottedReporting && dottedReportingAppraisal">
                                <span class="text-sm font-semibold mb-3">Dotted Line Manager</span>
                                <div class="w-full flex bg-white -mt-2"
                                  v-if="startAppraisal && dottedManagerScore && isDottedManager">
                                  <select v-if="item.isDotted" @change="
                                    changeDottedManagerRating(
                                      $event,
                                      item,
                                      templateKPATypes,
                                      kpaTypes.id,
                                      getReviewType()
                                    )
                                    " @input="setInput(kpaTypes.name)" class="input-select" v-model="item.ratingId" :disabled="appraisalStatus === 'submitted'">
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>
                                  <select
                                    v-else
                                    @change="
                                      onChangeDottedManagerRating(
                                        $event,
                                        item,
                                        templateKPATypes,
                                        kpaTypes.id,
                                        getReviewType()
                                      )
                                    "
                                    @input="setInput(kpaTypes.name)"
                                    class="input-select"
                                    :disabled="appraisalStatus === 'submitted'"
                                    v-model="item.dottedLineManagerRating"
                                  >
                                    <option value="" disabled selected>
                                      --Rating--
                                    </option>
                                    <option
                                      v-for="option in activeRatingOptions"
                                      :value="option.id"
                                      :key="option.id"
                                    >
                                      {{ option.name }}
                                    </option>
                                  </select>
                                </div>
                                <!-- <c-select placeholder="--Rating--" variant="w-full" class="-ml-1 -mt-4" type="number"
                                  :disabled="true" :options="activeRatingOptions" v-model="item.dottedLineManagerRating"
                                  v-else /> -->
                                <c-select
                                  :placeholder="
                                    item.dottedLineManagerRating
                                      ? `Appriased`
                                      : ``
                                  "
                                  variant="w-full"
                                  class="-ml-1 -mt-4"
                                  type="number"
                                  :disabled="true"
                                  v-else
                                  :options="activeRatingOptions"
                                />
                              </div>
                            </div>
                          </accordion>
                        </div>
                      </div>
                    </accordion>

                    <template>
                      <div
                        class="flex -mt-4 items-center border-b border-gray-200 py-2"
                      >
                        <span
                          class="text-sm font-bold text-gray-700 whitespace-nowrap"
                        >
                          FINAL SCORE
                        </span>

                        <div class="flex-1 h-px bg-gray-200 mx-4"></div>

                        <div class="flex items-center space-x-2">
                          <span
                            class="px-3 py-3 text-sm font-semibold text-gray-600 bg-gray-100 rounded-md"
                          >
                            APPROVED SCORE
                            <span class="font-bold ml-3 text-darkPurple">{{
                              approvedScore || "---"
                            }}</span>
                          </span>
                          <span
                            class="px-3 py-3 text-sm font-semibold text-gray-600 bg-gray-100 rounded-md"
                          >
                            SELF-APPRAISAL
                            <span class="font-bold ml-3 text-darkPurple">{{
                              selfAppraisalScore
                            }}</span>
                          </span>
                        </div>
                      </div>
                    </template>

                    <template v-if="loaded">
                      <div
                        class="w-full flex p-3 gap-4 justify-between items-center"
                        v-if="!appraisalEnd"
                      >
                        <div class="flex justify-start gap-2">
                          <Button
                            class="bg-dynamicBackBtn text-white"
                            width="15rem"
                            @click="submitAppraiser('active', finalAppraisal)"
                            style="height: 3rem"
                            v-if="
                              lineManagerAppraisalScore &&
                              appraisalStatus !== 'submitted'
                            "
                          >
                            <span class="mr-6 ml-6">Share with Appraisee</span>
                          </Button>

                          <Button
                            class="text-dynamicBackBtn border border-solid border-dynamicBackBtn text-center"
                            width="8rem"
                            @click="submitAppraiser('draft', finalAppraisal)"
                            v-if="appraisalState !== 'active'"
                          >
                            Save as Draft
                          </Button>

                          <Button
                            background-color="none"
                            class="ml-4"
                            style="color: #321c3b"
                            @onClick="goBackToDirectReport()"
                            v-if="appraisalStatus !== 'submitted'"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </template>
                  </ValidationObserver>
                </div>
              </div>
            </template>
          </div>

          <div class="w-full" v-if="appraisalMessage !== ''">
            <div class="w-full flex flex-col justify-center items-center mt-20">
              <icon icon-name="empty_agreement" size="l" style="width: 300px" />
              <div class="w-1/2 text-base text-center">
                {{ appraisalMessage }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template>
      <RightSideBar
        v-if="isOpenComments"
        @submit="sendComment"
        :disabled-button="isSavingComment"
        @close="(isOpenComments = false), (senderkpaId = ''), (comment = '')"
        button-class="bg-dynamicBackBtn text-white"
      >
        <template v-slot:title>
          <p class="text-darkPurple font-bold text-lg leading-5 p-0">
            Comments
          </p>
        </template>
        <div class="w-full flex mb-7">
          <div class="flex flex-col w-full" style="min-height: 70vh">
            <CommentsBox
              :comments="allComments"
              :senderId="senderId"
              @markRead="markAsRead($event)"
              @deleteComment="deleteComments($event)"
              :loading="loadComments"
            />
            <div class="-mb-12" style="margin-top: auto">
              <CTextarea
                placeholder="--Message--"
                variant="primary"
                class="mt-2 rounded-lg"
                value=""
                :row="8"
                :height="90"
                :col="30"
                :input="null"
                v-model="comment"
              />
            </div>
          </div>
        </div>
      </RightSideBar>

      <RightSideBar
        v-if="statusModal"
        @close="statusModal = false"
        @submit="saveReport"
        button-class="bg-dynamicBackBtn text-white"
        :disabled-button="isSavingReport"
        v-bind="
          senderId !== $route.params.userId
            ? { closeButton: true }
            : { submit: 'Save' }
        "
      >
        <template v-slot:title>
          <div class="w-full flex justify-between items-center">
            <span class="text-darkPurple font-bold text-lg leading-5 p-0"
              >Report New</span
            >
            <span
              v-if="senderId === $route.params.userId"
              class="text-blueCrayola font-bold text-sm uppercase cursor-pointer flex"
              @click="$router.push({ name: 'EssEmployeeStatusReports' })"
            >
              <Icon
                icon-name="history-icon"
                class="text-blueCrayola"
                size="xms"
              />
              History
            </span>
          </div>
        </template>
        <StatusReport
          :kpasItem="kpasItem"
          :kpaTypes="kpaTypes"
          :edit="true"
          :isView="senderId !== $route.params.userId"
          @update-files="updateFiles"
          @update-comment="updateComment"
          @update-support="updateSupport"
          @update-status="updateStatus"
          @kpis-criteriaValues="kpisCriteriaValues"
        />
      </RightSideBar>
    </template>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Tab from "@scelloo/cloudenly-ui/src/components/tab";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import Card from "@scelloo/cloudenly-ui/src/components/card";
import Loader from "@/components/Loader";
import Accordion from "@/components/Accordion";
import Icon from "@/components/Icon";
import RightSideBar from "@/components/RightSideBar";
import Button from "@/components/Button";
import CommentsBox from "@/components/commentsBox";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";
import StatusReportView from "./EmployeeStatusReport";
import StatusReport from "@/components/StatusReport";

export default {
  name: "PerformanceEmployeeAppraisals",
  mixins: [paramsMixin, timeFormatMixin],
  components: {
    ValidationObserver,
    StatusReportView,
    Tab,
    Breadcrumb,
    BackButton,
    CSelect,
    Button,
    CommentsBox,
    CText,
    Loader,
    Card,
    Accordion,
    Icon,
    CTextarea,
    RightSideBar,
    StatusReport,
  },
  data() {
    return {
      statusModal: false,
      openModal: false,
      isLoading: true,
      loaded: false,
      isOpenComments: false,
      loadComments: true,
      isManager: false,
      loadingAppraisal: true,
      hasDottedReporting: false,
      isDottedManager: false,
      dottedReportingAppraisal: false,
      midTermAppraisalStructure: "",
      tabsItems: [],
      activeTab: `${this.$route.params.cycle}`,
      KPAHeader: [
        { title: "KPA", value: "name" },
        { title: "Objective", value: "objective" },
        { title: "KPI", value: "kpis" },
        { title: "Weight (%)", value: "weight" },
      ],
      breadcrumbs: [
        {
          disabled: false,
          text: "Performance",
          href: "Performance",
          id: "Performance",
        },
        {
          disabled: false,
          text: "Performance Appraisals",
          href: "performance-appraisals",
          id: "PerformanceAppraisals",
        },
        {
          disabled: false,
          text: "Employee Appraisal",
          href: "employee-appraisal",
          id: "EmployeeAppraisal",
        },
      ],
      startAppraisal: false,
      scores: {},
      allocatedWeight: 0,
      appraisalStartDate: "",
      appraisalEndDate: "",
      appraisalMessage: "",
      appraisalEnd: false,
      templateKPATypes: [],
      inputChange: [],
      Ratings: [],
      ratingOptions: [],
      flagOptions: [],
      employeeAppraisals: [],
      selfAppraisalScore: "",
      lineManagerAppraisalScore: "",
      approvedScore: "---",
      appraisalStatus: "",
      appraisalFlag: "",
      appraisalState: "",
      dottedManagerScore: "",
      appraisalAgreed: false,
      checkEditAppraisal: false,
      disableAppraisal: false,
      appraisalDisplay: "",
      employee: false,
      finalAppraisal: false,
      cyclesArr: [],
      allComments: [],
      comment: "",
      employees: {},
      kpaAppraisalSummary: [],
      payload: {
        employeeAgreementId: "",
        orgId: null,
        goalId: null,
        year: "",
        employeeId: "",
        appraisal_status: "",
        status: "draft",
        cycleId: "",
        appraisalFor: "",
        appraisalBy: "",
        share: false,
        reviewType: "",
        kpa_types: [],
      },
      kpaTypeManagerScores: {},
      senderkpaId: "",
      managerId: "",
      senderId: "",
      kpasItem: {},
      kpaTypes: {},
      files: [],
      reportComment: "",
      requireSupport: "",
      reportStatus: "",
      currentAchievement: "",
      criteriaValues: "",
      managerScores: {},
      isDottedViewing: "",
      directReportYear: `${this.$route.params.directReportYear}`,
      directReportId: `${this.$route.params.directReportId}`,
      QAppraisal: `${this.$route.params.QAppraisal}`,
      isSavingComment: false,
      isSavingReport: false,
    };
  },

  watch: {
    templateKPATypes: {
      deep: true,
      immediate: true,
      async handler() {
        if (!this.templateKPATypes.length) {
          this.approvedScore = "---";
          return;
        }

        let totalManager = 0;
        let totalDotted = 0;
        let hasManager = false;
        let hasDotted = false;

        this.templateKPATypes.forEach((kpaType) => {
          if (kpaType.kpas) {
            kpaType.kpas.forEach((item) => {
              const manager =
                item.managerScore != null && item.managerScore !== ""
                  ? parseFloat(item.managerScore)
                  : null;
              const dotted =
                item.dottedLineManagerScore != null &&
                item.dottedLineManagerScore !== ""
                  ? parseFloat(item.dottedLineManagerScore)
                  : null;

              if (manager !== null && !isNaN(manager)) {
                totalManager += manager;
                hasManager = true;
              }

              if (dotted !== null && !isNaN(dotted)) {
                totalDotted += dotted;
                hasDotted = true;
              }
            });
          }
        });

        let finalScore = "---";
        const reviewType = this.getReviewType();

        if (!hasManager && !hasDotted) {
          this.approvedScore = "---";
          return;
        }

        let avg;
        if (hasManager && hasDotted) {
          avg = (totalManager + totalDotted) / 2;
        } else if (hasManager) {
          avg = totalManager;
        } else {
          avg = totalDotted;
        }

        if (reviewType === "mid_review") {
          try {
            if (avg.toFixed(2) > 0) {
              const response = await this.getAverageAppraisal(
                avg.toFixed(2),
                ""
              );
              finalScore = response || "---";
            }
          } catch (err) {
            finalScore = "---";
          }
        } else if (reviewType === "final_review") {
          finalScore = avg > 0 ? avg.toFixed(2) : "---";
        }

        this.approvedScore = finalScore;
      },
    },
  },

  computed: {
    activeRatingOptions() {
      if (!this.finalAppraisal) {
        if (this.midTermAppraisalStructure === "regular_ratings") {
          return this.ratingOptions;
        }
        return this.flagOptions;
      }
      return this.ratingOptions;
    },

    kpaTypeScores() {
      return this.managerScores;
    },
  },

  methods: {
    async calculateScores() {
      const scores = {};

      for (const kpaType of this.templateKPATypes) {
        if (!kpaType.kpas || kpaType.kpas.length === 0) {
          scores[kpaType.id] = "---";
          continue;
        }

        let totalManager = 0;
        let totalDotted = 0;
        let totalWeight = 0;
        let hasManager = false;
        let hasDotted = false;

        kpaType.kpas.forEach((item) => {
          const mScore =
            item.managerScore != null ? parseFloat(item.managerScore) : null;
          const dScore =
            item.dottedLineManagerScore != null
              ? parseFloat(item.dottedLineManagerScore)
              : null;
          const weight = parseFloat(item.weight) || 0;

          if (mScore != null) {
            totalManager += mScore;
            hasManager = true;
          }
          if (dScore != null) {
            totalDotted += dScore;
            hasDotted = true;
          }

          totalWeight += weight;
        });

        if (!hasManager && !hasDotted) {
          scores[kpaType.id] = "---";
          continue;
        }

        const reviewType = this.getReviewType();
        let finalScore;

        if (reviewType === "mid_review") {
          const managerResult = hasManager
            ? (totalManager / totalWeight) * 100
            : null;
          const dottedResult = hasDotted
            ? (totalDotted / totalWeight) * 100
            : null;

          if (managerResult != null && dottedResult != null) {
            finalScore = (managerResult + dottedResult) / 2;
          } else {
            finalScore = managerResult != null ? managerResult : dottedResult;
          }

          try {
            const response = await this.getAverageAppraisal(finalScore);
            scores[kpaType.id] = response || finalScore.toFixed(2);
          } catch (err) {
            console.error(err);
            scores[kpaType.id] =
              finalScore != null ? finalScore.toFixed(2) : "---";
          }
        } else {
          const managerResult = hasManager ? totalManager : null;
          const dottedResult = hasDotted ? totalDotted : null;

          if (managerResult != null && dottedResult != null) {
            finalScore = (managerResult + dottedResult) / 2;
          } else {
            finalScore = managerResult != null ? managerResult : dottedResult;
          }

          scores[kpaType.id] =
            finalScore != null ? finalScore.toFixed(2) : "---";
        }
      }

      this.managerScores = scores;
    },
    getKPIFlag(ratingId, flagOptions) {
      const filteredFlag = flagOptions.find(
        (flag) => flag.id === ratingId
      )?.name;
      return filteredFlag || "---";
    },

    getReviewType() {
      if (!this.finalAppraisal) {
        if (this.midTermAppraisalStructure === "regular_ratings") {
          return "final_review";
        }
        return "mid_review";
      }
      return "final_review";
    },

    activateTab(tab) {
      const tabIndex = this.tabsItems.indexOf(tab, 0);
      this.inputChange = [];

      if (tabIndex === this.tabsItems.length - 2) {
        this.finalAppraisal = true;
        this.payload.reviewType = "final_review";
      } else {
        this.finalAppraisal = false;
        this.payload.reviewType = "mid_review";
      }

      this.cyclesArr.filter((cycle) => {
        if (cycle.name === tab) {
          this.payload.cycleId = cycle.id;
        }
        return {};
      });

      this.employeeAppraisals = [];
      this.templateKPATypes = [];
      this.loaded = false;
      this.loadingAppraisal = true;
      this.startAppraisal = false;
      this.appraisalMessage = "";
      this.appraisalEnd = false;

      this.getEmployeeAppraisal();
      this.getAgreements();

      this.$_getAppraisalCycle(this.appraisalQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;

        appraisal[0].cycles.map((cycle) => {
          this.cyclesArr.push({
            id: cycle.id,
            name: cycle.name,
          });

          if (cycle.name === this.activeTab) {
            const appraisalStartDate = cycle.appraisal_starts;
            const appraisalEndDate = cycle.appraisal_ends;

            this.checkAppraisalStart(appraisalStartDate, appraisalEndDate);
          }
          return {};
        });
      });

      this.activeTab = tab;
    },

    setInput(kpaTypeName) {
      if (!this.inputChange.includes(kpaTypeName)) {
        this.inputChange.push(kpaTypeName);
      }
    },

    changeRating(event, item, kpaTypes) {
      const flagId = event.target.value;
      const { kpaId } = item.data;
      let sum = 0;
      this.$_calculateRatingScore(kpaId, flagId).then((response) => {
        const itemVariable = item;
        itemVariable.data.selfScore = response.data.data.score;
        kpaTypes.forEach((type) => {
          type.kpas.forEach((items) => {
            sum += Number(items.selfScore);
          });
        });
        this.selfAppraisalScore = sum.toFixed(2);
      });
    },
    async onChangeLineManagerRating(
      event,
      item,
      kpaTypes,
      kpaTypeId,
      reviewType
    ) {
      this.$set(item, "hasChanged", true);
      item.ratingId = event.target.value;
      await this.changeManagerRating(
        event,
        item,
        kpaTypes,
        kpaTypeId,
        reviewType
      );
    },
    async changeManagerRating(event, item, kpaTypes, kpaTypeId, reviewType) {
      await this.updateScore(
        event.target.value,
        item,
        kpaTypes,
        kpaTypeId,
        reviewType,
        "manager"
      );
    },
    async onChangeDottedManagerRating(
      event,
      item,
      kpaTypes,
      kpaTypeId,
      reviewType
    ) {
      this.$set(item, "isDotted", true);
      item.ratingId = event.target.value;
      await this.updateScore(
        event.target.value,
        item,
        kpaTypes,
        kpaTypeId,
        reviewType,
        "dottedLineManager"
      );
    },
    async changeDottedManagerRating(
      event,
      item,
      kpaTypes,
      kpaTypeId,
      reviewType
    ) {
      await this.updateScore(
        event.target.value,
        item,
        kpaTypes,
        kpaTypeId,
        reviewType,
        "dottedLineManager"
      );
    },

    async updateScore(
      ratingId,
      item,
      kpaTypes,
      kpaTypeId,
      reviewType,
      scoreType
    ) {
      try {
        const response = await this.$_calculateRatingScore(
          item.kpaId,
          ratingId,
          reviewType
        );
        const { score } = response.data.data;
        const { rate } = response.data.data;

        // Update the item score first
        if (reviewType === "mid_review") {
          item[`${scoreType}Score`] = score;
          item[`${scoreType}Rating`] = response.data.data.rank.flag.name;
        } else {
          item[`${scoreType}Score`] = score;
          item[`${scoreType}Rating`] = score;
        }

        await this.calculateScores();

        if (!this.hasDottedReporting || !this.dottedReportingAppraisal) {
          if (reviewType === "mid_review") {
            item.approvedFlag = response.data.data.rank.flag.name || "---";
          } else {
            item.approvedScore = score.toFixed(2) || "---";
          }
        } else if (scoreType === "manager") {
          if (reviewType === "mid_review") {
            let avgScore;
            if (item.dottedLineManagerScore !== null) {
              avgScore = (rate + item.dottedLineManagerRatingPoint) / 2;
            } else {
              // only manager rate
              avgScore = rate;
            }

            const data = await this.getAverageAppraisal(avgScore.toFixed(2));
            item.approvedFlag = data || "---";
          }

          let avgScore;
          if (item.dottedLineManagerScore !== null) {
            avgScore = (score + Number(item.dottedLineManagerScore)) / 2;
          } else {
            // only manager score
            avgScore = score;
          }

          item.approvedScore =
            avgScore != null ? Number(avgScore).toFixed(2) : "---";
        } else if (scoreType === "dottedLineManager") {
          if (reviewType === "mid_review") {
            let avgScore;
            if (
              item.managerRatingPoint !== "" ||
              item.managerRatingPoint !== null
            ) {
              avgScore = (rate + item.managerRatingPoint) / 2;
            } else {
              // only dottedLine rate
              avgScore = rate;
            }

            const data = await this.getAverageAppraisal(avgScore.toFixed(2));
            item.approvedFlag = data || "---";
          }

          let avgScore;
          if (item.managerScore != null) {
            avgScore = (score + Number(item.managerScore)) / 2;
          } else {
            // only dottedLine score
            avgScore = score;
          }

          item.approvedScore =
            avgScore != null ? Number(avgScore).toFixed(2) : "---";
        }

        // 🔄 Force recomputation of computed properties if needed
        this.$delete(this.kpaTypeManagerScores, kpaTypeId);
        this.recalculateKPAScore(kpaTypes, kpaTypeId, scoreType);
      } catch (error) {
        // Error updating score: error
      }
    },

    recalculateKPAScore(kpaTypes, kpaTypeId) {
      const kpaType = kpaTypes.find((type) => type.id === kpaTypeId);
      if (!kpaType || !kpaType.kpas) return;

      const totalScore = kpaType.kpas.reduce(
        (sum, item) => sum + (parseFloat(item.managerScore) || 0),
        0
      );

      const totalWeight = kpaType.kpas.reduce(
        (sum, item) => sum + (parseFloat(item.weight) || 0),
        0
      );

      if (this.getReviewType() === "mid_review") {
        const weightedScore = (totalScore / totalWeight) * 100;
        this.calculateAndCacheScore(weightedScore, kpaTypeId);
      }
    },

    async calculateAndCacheScore(weightedScore, kpaTypeId) {
      try {
        const flag = await this.getAverageAppraisal(weightedScore);
        this.$set(this.kpaTypeManagerScores, kpaTypeId, flag || "---");
      } catch (error) {
        this.$set(this.kpaTypeManagerScores, kpaTypeId, "---");
      }
    },

    async getAverageAppraisal(score) {
      try {
        const { data } = await this.$_getScoreAppraisalFlag(score);
        return data.ranking.flag.name;
      } catch (error) {
        return "---";
      }
    },

    getAverageSummary(id, kpaTypeName) {
      let summary;
      let score;
      const appraisalSummary = [];

      this.employeeAppraisals.forEach((appraisal) => {
        appraisal.summary.forEach((el) => {
          if (el.managerFlag) {
            appraisalSummary.push(el.kpaTypeName);
          }
        });
      });

      if (
        appraisalSummary.includes(kpaTypeName) &&
        !this.inputChange.includes(kpaTypeName)
      ) {
        this.kpaAppraisalSummary = [];
        summary = this.getKPATotalManagerAppraisal(kpaTypeName).summary;
        score = this.getKPATotalManagerAppraisal(kpaTypeName).score;
      } else {
        this.kpaAppraisalSummary.forEach((kpa) => {
          if (kpa.id === id) {
            summary = kpa.summary;
            score = kpa.score;
          }
        });
      }

      if (summary) {
        if (score) {
          return `${summary} (${score})`;
        }
        return summary;
      }
      if (score) {
        return `(${score})`;
      }
      return "---";
    },

    async scoreFlag(item) {
      const varItem = item || null;

      const score = (item.data.managerScore / item.data.weight) * 100;

      try {
        const { data } = await this.$_getScoreAppraisalFlag(score);
        varItem.data.outcome = data.ranking.description.description;
      } catch (err) {
        throw new Error(err);
      }
    },

    getKPATotalScore(kpaId, kpaTypes) {
      if (kpaTypes.id !== kpaId) return "";

      const kpaArray = kpaTypes.kpas || [];

      const hasValidScore = kpaArray.some(
        (item) => item.selfScore !== null && item.selfScore !== "0.00"
      );

      if (!hasValidScore) return "";

      const sum = kpaArray.reduce(
        (acc, item) => acc + (parseFloat(item.selfScore) || 0),
        0
      );

      this.currentScoreValue = sum;

      return sum.toFixed(2);
    },

    getKPATotalManagerScore(kpaId, kpaTypes) {
      if (kpaTypes.id !== kpaId) return "";

      const kpaArray = kpaTypes.kpas || [];

      const hasNonNullScore = kpaArray.some(
        (item) => item.managerScore !== null && item.managerScore !== "0.00"
      );

      if (!hasNonNullScore) return "";

      const sum = kpaArray.reduce(
        (acc, item) => acc + (parseFloat(item.managerScore) || 0),
        0
      );

      return sum.toFixed(2);
    },

    getKPATotalManagerAppraisal(kpaTypesname) {
      const result = { summary: "---", score: "---" };

      this.employeeAppraisals.forEach((appraisal) => {
        if (appraisal.cycle === this.activeTab) {
          appraisal.summary.forEach((summary) => {
            if (summary.kpaTypeName === kpaTypesname) {
              result.summary = summary.managerFlag || "---";
              result.score = summary.managerKpaTypeScore || "---";
            }
          });
        }
      });

      return result;
    },

    getKPATotalSelfAppraisal(kpaTypesname) {
      const result = { summary: null, score: null };

      this.employeeAppraisals.forEach((appraisal) => {
        if (appraisal.cycle === this.activeTab) {
          appraisal.summary.forEach((summary) => {
            if (summary.kpaTypeName === kpaTypesname) {
              result.summary = summary.employeeFlag || "---";
              result.score = summary.employeeKpaTypeScore || "---";
            }
          });
        }
      });

      return this.getReviewType() === "final_review"
        ? result.score != null
          ? Number(result.score).toFixed(2)
          : "---"
        : result.summary ?? "---";
    },

    submitAppraiser(state, finalAppraisal) {
      const kpaData = [];
      const validateKPA = [];
      this.payload.orgId = this.$orgId;
      this.payload.appraisal_status = "pending";
      this.payload.year = this.$route.params.year;
      this.payload.employeeId = this.$route.params.userId;
      this.payload.appraisalFor = this.$route.params.userId;

      if (finalAppraisal) {
        this.payload.appraisal_agreement = "employee_agreed";
      }

      if (state === "draft") {
        this.payload.status = state;
      } else {
        this.payload.status = "active";
        this.payload.share = true;
      }
      this.templateKPATypes.forEach((kpaType) => {
        const newKPAs = kpaType.kpas.map(
          ({
            name,
            objective,
            kpis,
            weight,
            selfRating,
            selfScore,
            managerRating,
            managerScore,
            approvedScore,
            approvedFlag,
            dottedLineManagerFlag,
            dottedLineManagerRating,
            ...rest
          }) => ({
            ...rest,
          })
        );
        const validation = newKPAs.every((item) => item.ratingId);
        validateKPA.push(validation);
        kpaData.push({
          kpa_types_id: kpaType.id,
          kpas: newKPAs,
        });
      });
      if (validateKPA.every((valid) => valid === true)) {
        this.payload.kpa_types = kpaData;
        this.payload.appraisal_agreement = "employee_agreed";
        this.payload.manager_employee_agreed = true;

        if (this.isDottedManager) {
          this.payload.dotted_line_manager_agreed = true;
          this.$_setDottedManagerAppraisal(this.payload)
            .then((response) => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.goBackToDirectReport();
            })
            .catch((err) => {
              this.$toasted.error(err.response.data.message, {
                duration: 5000,
              });
            });
        } else {
          this.payload.manager_employee_agreed = true;
          this.$_setManagerAppraisal(this.payload)
            .then((response) => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.goBackToDirectReport();
            })
            .catch((err) => {
              this.$toasted.error(err.response.data.message, {
                duration: 5000,
              });
            });
        }
      } else {
        this.$toasted.error("No scores set for each KPA", { duration: 5000 });
      }
    },
    goBackToDirectReport() {
      this.$router.push({
        name: "EssAprraisalsDirectReports",
        params: {
          id: this.directReportId,
          year: this.directReportYear,
          QAppraisal: this.QAppraisal,
        },
      });
    },
    checkSelected(value) {
      if (value === "Approve") {
        this.approveAppraisal();
      } else {
        this.submitAppraiser("active", this.finalAppraisal);
      }
    },

    approveAppraisal() {
      const requestPayload = {
        appraisalIds: [this.payload.appraisalId],
        appraisal_status: "submitted",
        userId: this.$AuthUser.id,
        orgId: this.$orgId,
        type: this.isDottedManager
          ? "dotted_line_managers"
          : "direct_line_managers",
      };
      this.$_approveAppraisal(requestPayload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.$router.back();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    getUserDetails() {
      this.$_getOneEmployeeAll({
        id: this.$route.params.userId,
      }).then((response) => {
        this.employees = {
          id: response.data.userId,
          name: `${response.data.fname} ${response.data.lname}`,
          photo: response.data.photo,
          designation: response.data.userDesignation?.name || "N/A",
          function: response.data.orgFunction?.name || "N/A",
          manager: response.data.userReportingTo
            ? `${response.data.userReportingTo.fname} ${response.data.userReportingTo.lname}`
            : "N/A",
          employeeId: response.data.employeeId || "N/A",
          office: response.data.employeeOffice?.name || "N/A",
        };
        this.hasDottedReporting =
          !!response.data.userDesignation.dottedReportingTo;
        this.isDottedManager = response.data.dottedReportingTo
          ? response.data.dottedReportingTo === this.$AuthUser.id
          : false;
        this.isLoading = false;
      });
    },

    getEmployeeAppraisal() {
      this.employeeAppraisals = [];

      this.selfAppraisalScore = "";
      this.lineManagerAppraisalScore = "";
      this.approvedScore = "";
      this.appraisalStatus = "";
      this.appraisalState = "";
      this.dottedManagerScore = "";

      this.$_getEmployeeAppraisal(this.$route.params.userId).then((result) => {
        const responseData = result.data.EmployeeAppraisal;

        responseData.forEach((response) => {
          if (response.appraisal.current_cycle) {
            if (this.finalAppraisal) {
              if (response.appraisal.current_cycle.name === this.activeTab) {
                this.selfAppraisalScore = Number(
                  response.appraisal.self_score
                ).toFixed(2);
                this.lineManagerAppraisalScore = Number(
                  response.appraisal.manager_score
                ).toFixed(2);
                this.dottedManagerScore = Number(
                  response.appraisal.dotted_manager_score
                ).toFixed(2);
                this.approvedScore = Number(
                  response.appraisal.approved_score
                ).toFixed(2);
                this.appraisalStatus = response.appraisal.appraisal_status;
                this.appraisalState = response.appraisal.appraisal_state;
                this.payload.appraisalId = response.appraisal.id
                  ? response.appraisal.id
                  : null;
                this.appraisalAgreed =
                  response.appraisal.manager_employee_agreed;
              }
            }

            if (!this.finalAppraisal) {
              if (response.appraisal.current_cycle.name === this.activeTab) {
                this.selfAppraisalScore =
                  this.getReviewType() === "final_review"
                    ? `${response.appraisalSummary.employeeFlagScore}`
                    : `${response.appraisalSummary.employeeFlag}`;
                this.lineManagerAppraisalScore =
                  this.getReviewType() === "final_review"
                    ? `${response.appraisalSummary.managerFlagScore}`
                    : `${response.appraisalSummary.managerFlag}`;
                this.dottedManagerScore =
                  this.getReviewType() === "final_review"
                    ? `${response.appraisalSummary.dottedLineManagerFlagScore}`
                    : `${response.appraisalSummary.dottedLineManagerFlag}`;
                this.approvedScore = response.appraisalSummary.managerFlag;
                this.appraisalStatus = response.appraisal.appraisal_status;
                this.appraisalState = response.appraisal.appraisal_state;
                this.payload.appraisalId = response.appraisal.id
                  ? response.appraisal.id
                  : null;
                this.appraisalAgreed = "";
              }
            }
            response.appraisal.performance_scores.map((score) => {
              this.employeeAppraisals.push({
                cycle: response.appraisal.current_cycle.name,
                id: score.kpaId,
                selfRatingPoint: score.selfRatingPoint,
                flagId: score.ranking.flag.name,
                managerFlag: score.manager_ranking
                  ? score.manager_ranking.flag.name
                  : "",
                selfScore: score.selfScore,
                outcome: score.outcome,
                managerRatingPoint: score.managerRatingPoint,
                managerScore: score.managerScore,
                dottedLineManagerRatingPoint:
                  score.dottedLineManagerRatingPoint,
                dottedLineManagerScore: score.dottedLineManagerScore,
                dottedLineManagerFlag: score.dotted_line_manager_ranking
                  ? score.dotted_line_manager_ranking.flag.name
                  : "",
                summary: response.summary,
              });

              return {};
            });
          }
        });

        this.getAgreements();
      });
    },

    formatEmployeeDate(value) {
      let initialDate = "";
      if (value) {
        initialDate = new Date(value).toISOString().replace(/.\d+Z$/g, "");
        return this.$DATEFORMAT(new Date(initialDate), "MMMM dd, yyyy");
      }
      return "";
    },

    async getAgreements() {
      const { userId } = this.$route.params;
      const year = Number(this.$route.params.year);
      const { goalId } = this.$route.params;

      const response = await this.$_getEmployeeAgreement(userId);
      const myAgreements = response.data.EmployeePerformanceAgreements;
      this.managerId = response.data.employee.lineManager.id;

      const agreement = myAgreements.find((a) => Number(a.year) === year);

      if (!agreement) {
        this.isLoading = false;
        this.loaded = true;
        this.loadingAppraisal = false;
        return;
      }

      const agreementKPA = agreement.employee_agreement_kpa;
      this.payload.employeeAgreementId = agreement.id;
      this.payload.goalId = goalId;

      const result = await this.$_getOneTemplate(agreement.templateId);
      this.templateKPATypes = result.data.PerformanceTemplate.kpa_types;

      let totalWeight = 0;
      this.templateKPATypes = await Promise.all(
        this.templateKPATypes.map(async (kpaType) => {
          const newKpaType = { ...kpaType, kpas: [] };

          const matchingKPAs = agreementKPA.filter(
            (kpi) => kpi.kpaTypeId === kpaType.id
          );

          const kpas = await Promise.all(
            matchingKPAs.map(async (employeeKpi) => {
              const kpa = {
                name: employeeKpi.name,
                objective: employeeKpi.objective,
                kpaId: employeeKpi.id,
                kpis: this.getCriteriaData(employeeKpi.kpis_employee),
                weight: employeeKpi.weight,
                outcome: this.getOutcome(employeeKpi.id),
                selfRating: this.getSelfRating(employeeKpi.id),
                selfScore: this.getScore(employeeKpi.id),
                ratingId: this.getRatingId(employeeKpi.id),
                managerRating: this.getManagerRating(employeeKpi.id),
                managerScore: this.getManagerScore(employeeKpi.id, employeeKpi),
                managerRatingPoint: this.getManagerRatingPoint(employeeKpi.id),
                approvedScore: await this.getApprovedScore(employeeKpi.id),
                approvedFlag: await this.getApprovedFlag(employeeKpi.id),
                ...(this.dottedReportingAppraisal &&
                  this.hasDottedReporting && {
                    dottedLineManagerRating: this.getDottedManagerRating(
                      employeeKpi.id
                    ),
                    dottedLineManagerScore: this.getDottedManagerScore(
                      employeeKpi.id
                    ),
                    dottedLineManagerRatingPoint: this.getDottedRatingPoint(
                      employeeKpi.id
                    ),
                  }),
                report: employeeKpi.report,
                hasChanged: false,
                isDotted: false,
              };
              totalWeight += Number(employeeKpi.weight) || 0;
              return kpa;
            })
          );

          newKpaType.kpas = kpas;
          newKpaType.selfSummary = null;
          return newKpaType;
        })
      );

      this.allocatedWeight = totalWeight;
      this.loaded = true;
      this.loadingAppraisal = false;
      this.calculateScores();
      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    },

    async getApprovedScore(kpasid) {
      const appraisal = this.employeeAppraisals.find(
        (a) => a.id === kpasid && a.cycle === this.activeTab
      );

      if (
        !appraisal ||
        (appraisal.managerScore == null &&
          (this.hasDottedReporting
            ? appraisal.dottedLineManagerScore == null
            : true))
      ) {
        return null; // or '---'
      }

      const scores = [];
      if (appraisal.managerScore != null) {
        scores.push(Number(appraisal.managerScore));
      }
      if (appraisal.dottedLineManagerScore != null) {
        scores.push(Number(appraisal.dottedLineManagerScore));
      }

      let score;

      if (scores.length > 1) {
        score = (scores[0] + scores[1]) / 2;
      } else {
        score = this.hasDottedReporting ? scores[0] / 2 : scores[0];
      }

      return score.toFixed(2);
    },

    async getApprovedFlag(kpasid) {
      const appraisal = this.employeeAppraisals.find(
        (a) => a.id === kpasid && a.cycle === this.activeTab
      );

      if (
        !appraisal ||
        (appraisal.managerScore == null &&
          appraisal.dottedLineManagerScore == null)
      ) {
        return null; // or '---'
      }

      const scores = [];
      if (appraisal.managerScore != null) {
        scores.push(Number(appraisal.managerRatingPoint));
      }
      if (appraisal.dottedLineManagerScore != null) {
        scores.push(Number(appraisal.dottedLineManagerRatingPoint));
      }

      const score =
        scores.length > 1
          ? (scores[0] + scores[1]) / 2 // average if both exist
          : scores[0];

      const { data } = await this.$_getScoreAppraisalFlag(score.toFixed(2));
      return data.ranking?.flag.name;
    },

    getScore(kpasid) {
      const appraisal = this.employeeAppraisals.find(
        (a) => a.id === kpasid && a.cycle === this.activeTab
      );

      if (!appraisal || appraisal.selfScore == null) {
        return 0;
      }

      return this.getReviewType() === "final_review"
        ? Number(appraisal.selfScore).toFixed(2)
        : appraisal.flagId;
    },

    getSelfRating(kpasid) {
      let ratePoint;
      let rating;
      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          if (this.finalAppraisal) {
            return (ratePoint = appraisal.selfRatingPoint);
          }
          if (this.midTermAppraisalStructure === "regular_ratings") {
            ratePoint = appraisal.selfRatingPoint;
          } else {
            ratePoint = appraisal.flagId;
          }
        }
      });

      // regular_ratings
      if (
        this.finalAppraisal ||
        this.midTermAppraisalStructure === "regular_ratings"
      ) {
        this.ratingOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      } else {
        this.flagOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      }
      return rating || "";
    },

    getDottedManagerRating(kpasid) {
      let ratePoint;
      let rating;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          if (this.finalAppraisal) {
            return (ratePoint = appraisal.dottedLineManagerRatingPoint);
          }
          if (this.midTermAppraisalStructure === "regular_ratings") {
            ratePoint = appraisal.dottedLineManagerRatingPoint;
          } else {
            ratePoint = appraisal.dottedLineManagerFlag;
          }
        }
      });

      if (
        this.finalAppraisal ||
        this.midTermAppraisalStructure === "regular_ratings"
      ) {
        this.ratingOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      } else {
        this.flagOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      }
      return rating || "";
    },

    getDottedRatingPoint(kpasid) {
      let ratePoint;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          ratePoint = appraisal.dottedLineManagerRatingPoint;
        }
        return {};
      });

      return ratePoint || "";
    },

    getManagerRatingPoint(kpasid) {
      let ratePoint;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          ratePoint = appraisal.managerRatingPoint;
        }
        return {};
      });

      return ratePoint || "";
    },

    getDottedManagerScore(kpasid) {
      const appraisal = this.employeeAppraisals.find(
        (a) => a.id === kpasid && a.cycle === this.activeTab
      );

      if (!appraisal || appraisal.dottedLineManagerScore == null) {
        return null;
      }

      return Number(appraisal.dottedLineManagerScore).toFixed(2);
    },

    getManagerRating(kpasid) {
      let ratePoint;
      let rating;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          if (this.finalAppraisal) {
            return (ratePoint = appraisal.managerRatingPoint);
          }
          if (this.midTermAppraisalStructure === "regular_ratings") {
            ratePoint = appraisal.managerRatingPoint;
          } else {
            ratePoint = appraisal.managerFlag;
          }
        }
      });
      if (
        this.finalAppraisal ||
        this.midTermAppraisalStructure === "regular_ratings"
      ) {
        this.ratingOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      } else {
        this.flagOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
        });
      }
      return rating || "";
    },

    getOutcome(kpasid) {
      let outcome;
      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          outcome = appraisal.outcome;
        }
        return {};
      });
      return outcome || "";
    },

    getManagerScore(kpasid) {
      const appraisal = this.employeeAppraisals.find(
        (a) => a.id === kpasid && a.cycle === this.activeTab
      );

      if (!appraisal || appraisal.managerScore == null) {
        return null;
      }

      return Number(appraisal.managerScore).toFixed(2);
    },

    getRatingId(kpasid) {
      let ratePoint;
      let rating;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          if (
            this.finalAppraisal ||
            (!this.finalAppraisal &&
              this.midTermAppraisalStructure === "regular_ratings")
          ) {
            if (this.isDottedManager) {
              ratePoint = appraisal.dottedLineManagerRatingPoint;
            } else {
              ratePoint = appraisal.managerRatingPoint;
            }
          } else {
            if (this.isDottedManager) {
              ratePoint = appraisal.dottedLineManagerFlag;
            } else {
              ratePoint = appraisal.managerFlag;
            }
          }
        }
        return {};
      });
      if (
        this.finalAppraisal ||
        (!this.finalAppraisal &&
          this.midTermAppraisalStructure === "regular_ratings")
      ) {
        this.ratingOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
          return {};
        });
      } else {
        this.flagOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
          return {};
        });
      }
      return rating || "";
    },

    getDottedRatingId(kpasid) {
      let ratePoint;
      let rating;

      this.employeeAppraisals.filter((appraisal) => {
        if (appraisal.id === kpasid && appraisal.cycle === this.activeTab) {
          if (this.finalAppraisal) {
            ratePoint = appraisal.dottedLineManagerRatingPoint;
          } else {
            ratePoint = appraisal.dottedLineManagerFlag;
          }
        }
        return {};
      });

      if (this.finalAppraisal) {
        this.ratingOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
          return {};
        });
      } else {
        this.flagOptions.filter((ratings) => {
          if (ratings.name === ratePoint) {
            rating = ratings.id;
          }
          return {};
        });
      }
      return rating || "";
    },

    getCriteriaData(kpis) {
      const criteriaData = [];
      const sortedRatings = this.Ratings.sort((a, b) => a.rating - b.rating);
      const sortedKPIS = kpis.sort((a, b) => a.score.rating - b.score.rating);
      this.Ratings = sortedRatings;
      let kpisVar = kpis;
      kpisVar = sortedKPIS;
      this.Ratings.map((rating, index) => {
        criteriaData.push({
          id: kpisVar[index].id,
          performanceRatingId: rating.id,
          criteria: kpisVar[index].criteria,
        });
        return {};
      });
      return criteriaData;
    },

    checkAppraisalStart(startDate, endDate) {
      const today = new Date();

      const appraisalStartDate = this.setDates(startDate);
      const appraisalEndDate = this.setDates(endDate);

      appraisalEndDate.setDate(appraisalEndDate.getDate() + 1);

      this.appraisalStartDate = appraisalStartDate.toISOString();
      this.appraisalEndDate = this.setDates(endDate).toISOString();

      if (today >= appraisalStartDate && today <= appraisalEndDate) {
        this.startAppraisal = true;
      } else if (today < appraisalStartDate && today <= appraisalEndDate) {
        this.startAppraisal = false;
        this.appraisalMessage = `${this.activeTab} appraisal starts on the
                          ${this.$DATEFORMAT(
                            new Date(this.appraisalStartDate),
                            "MMMM dd, yyyy"
                          )}. You will be notified when it begins.`;
      } else {
        this.startAppraisal = true;
        this.appraisalMessage = ``;
        this.appraisalEnd = true;
      }

      this.getEmployeeAppraisal();
    },

    getCycleTabs() {
      this.tabsItems = [];

      this.$_getAppraisalCycle(this.appraisalQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;

        const appraisalCycle = appraisal[0].cycles.sort(
          (a, b) =>
            this.setDates(a.appraisal_starts) -
            this.setDates(b.appraisal_starts)
        );
        this.tabsItems = appraisalCycle.map(({ name }) => name);
        this.tabsItems.push("Status Report Overview");

        appraisalCycle.forEach((cycle) => {
          if (cycle.name === this.$route.params.cycle) {
            this.payload.cycleId = cycle.id;
          }
        });
      });
    },

    getSettings() {
      this.$_getPerformanceGeneralSetting().then((response) => {
        response.data.PerformanceSettings.map((setting) => {
          const appraisalByWho = setting.appraisailScoringPreference;
          this.checkEditAppraisal = setting.managerCanEditEmployeeAppraisal;
          this.dottedReportingAppraisal = setting.appraisalReviewer.includes(
            "dotted_line_managers"
          );
          this.midTermAppraisalStructure = setting.midTermAppraisalMethod;

          if (appraisalByWho === "manager_to_appraisee") {
            this.appraisalDisplay = "manager";
            this.KPAHeader.push(
              {
                title: "First Line Manager’s Rating",
                value: "managerRating",
              },
              {
                title: "Performance Outcome",
                value: "outcome",
              }
            );
          } else if (appraisalByWho === "employee_self_appraisal") {
            this.appraisalDisplay = "employee";
            this.KPAHeader.push(
              {
                title: "Self Rating",
                value: "selfRating",
              },
              {
                title: "Performance Outcome",
                value: "outcome",
              }
            );
          } else if (appraisalByWho === "both") {
            this.appraisalDisplay = "both";
            this.KPAHeader.push(
              {
                title: "Self Rating",
                value: "selfRating",
              },
              {
                title: "First Line Manager’s Rating",
                value: "managerRating",
              },
              {
                title: "Performance Outcome",
                value: "outcome",
              }
            );
          }
          return {};
        });
      });
    },

    getUser() {
      this.$_getUser(this.$AuthUser.id).then((result) => {
        this.loadingData = false;
        if (result.data.user.userDesignation.reportingTo !== null) {
          this.employee = false;
        } else {
          this.employee = false;
        }
      });
    },

    formatCommentTime(date) {
      const newDate = new Date(date);
      const hours = newDate.getUTCHours();
      const minutes = newDate.getMinutes();
      return `${1 + ((hours - 1) % 12)}:${minutes
        .toString()
        .padStart(2, "0")} ${hours > 11 ? "PM" : "AM"}`;
    },

    formatCommentDate(date) {
      const newDate = new Date(date);
      return newDate.toDateString();
    },

    async getEachAgreementComments(kpiId = "") {
      this.senderkpaId = kpiId;
      const kpi = kpiId ? `?kpiId=${kpiId}` : "";
      this.loadComments = true;
      try {
        const { data } = await this.$_getEachAgreementComments(
          this.payload.employeeAgreementId,
          kpi
        );
        this.allComments = data.comments;
        this.isOpenComments = true;
        this.loadComments = false;
      } catch (error) {
        this.loadComments = false;
        this.senderkpaId = "";
        this.$toasted.error("Error getting comments", { duration: 5000 });
      }
    },
    async sendComment() {
      this.isSavingComment = true;
      const payload = {
        comment: this.comment,
        senderId: this.senderId, // userId of sender
        receiverId: this.managerId, // userId of receiver
        sendTo: "manager",
        performanceAgreementId: this.payload.employeeAgreementId,
        ...(this.senderkpaId && { kpiId: this.senderkpaId }),
      };

      try {
        await this.$_sendAgreementComments(payload);
        this.allComments = [];
        this.comment = "";
        this.isOpenComments = false;
        this.isSavingComment = false;
        this.$toasted.success("Comment successfully sent", { duration: 5000 });
      } catch (error) {
        this.isSavingComment = false;
        this.$toasted.error("Error sending comment", { duration: 5000 });
      }
    },

    deleteComments(id) {
      this.$_deleteAppraisalComments(id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.getComments(this.payload.employeeAgreementId);
        })
        .then((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    markAsRead(commentId) {
      const payload = {
        commentId,
        readStatus: true,
      };
      this.$_markAsRead(payload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.getComments(this.payload.employeeAgreementId);
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    getAppraisalFlags() {
      this.$_getAppraisalFlag().then((response) => {
        const { flags } = response.data;
        flags.forEach((flag) => {
          this.flagOptions.push({
            id: flag.id,
            name: flag.name,
          });
        });
      });
    },
    updateFiles(files) {
      this.files = files;
    },
    updateSupport(support) {
      this.requireSupport = support;
    },
    updateComment(comment) {
      this.reportComment = comment;
    },
    updateStatus(status) {
      this.reportStatus = status;
    },
    kpisCriteriaValues(criteriaValues) {
      this.criteriaValues = criteriaValues;
    },
    async saveReport() {
      this.isSavingReport = true;
      const payload = {
        orgId: this.$orgId,
        employeeId: this.senderId,
        employeeAgreementId: this.payload.employeeAgreementId,
        kpaTypeId: this.kpaTypes.id,
        kpiId: this.kpasItem.kpaId,
        status: this.reportStatus,
        criteria: this.criteriaValues,
        currentAchievement: "",
        requireSupport: this.requireSupport === "yes" ? true : false,
        comment: this.reportComment,
        files: this.files,
        ...(this.kpasItem.report &&
          this.kpasItem.report !== "null" && {
            reportId: this.kpasItem.report.id,
          }),
      };

      try {
        await this.$_saveReport(payload);
        await this.getAgreements();
        this.statusModal = false;
        this.isSavingReport = false;
        this.$toasted.success("Report done successfully", { duration: 5000 });
      } catch (error) {
        this.isSavingReport = false;
        this.$toasted.error(error.response.data.message, { duration: 5000 });
      }
    },
    async viewReport(kpasItem, kpaTypes) {
      this.statusModal = true;
      this.kpasItem = kpasItem;
      this.kpaTypes = kpaTypes;
    },
  },

  mounted() {
    this.senderId = this.$AuthUser.id;
    if (this.$route.params.userId === this.$AuthUser.id) {
      this.payload.appraisalFor = this.$AuthUser.id;
      this.payload.appraisalBy = this.$AuthUser.id;
      this.payload.employeeId = this.$AuthUser.id;
    } else {
      this.payload.appraisalFor = this.$route.params.userId;
      this.payload.appraisalBy = this.$AuthUser.id;
      this.payload.employeeId = this.$route.params.userId;
    }

    this.getSettings();
    this.getCycleTabs();
    this.$_getPerfomanceRating().then((response) => {
      this.Ratings = response.data.PerformanceRatings;
      response.data.PerformanceRatings.map((ratings) => {
        this.ratingOptions.push({
          id: ratings.id,
          name: ratings.rating,
        });
        return {};
      });
    });
    this.getUser();
    this.getAppraisalFlags();

    this.getUserDetails();
  },
};
</script>

<style>
.fit-content {
  width: fit-content;
}

.input-select:disabled {
  background: #eeeeee;
  color: #878e99;
  border: none;
  background-image: none;
  background-repeat: no-repeat;
  background-position-x: 95%;
  background-position-y: 12px;
}

.input-select {
  border: 1px solid #878e99;
  height: 38px;
  padding: 10px 15px;
  width: 100%;
  border-radius: 5px;
  color: #321c3b;
  line-height: 120%;
  letter-spacing: 0.015em;
}

.input-select:focus {
  outline: none;
}

.input-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  background-image: url(/img/down-arrow.f3bc3f05.svg);
  background-repeat: no-repeat;
  background-position-x: 95%;
  background-position-y: 14px;
  padding: 3px 15px;
  width: 100%;
  padding-right: 29px;
  outline: none;
  border: 1px solid #878e99;
  box-sizing: border-box;
  border-radius: 3px;
  height: 40px;
  /* width: 100%; */
  /* width: fit-content; */
}
</style>
